import { Component, Entity, registerComponent } from "aframe";
import { Vector3 } from "three";



interface GridData
{
	width : number;
	height : number;
	depth : number;
	columns : number;
	rows : number;
	layers : number;
	offset : number;
	radius : { x : number; y : number };
	size : { x:number; y:number; z:number; };
	dimension : { x:number; y:number; z:number; };
	align : { x:number; y:number; z:number; };
}

interface Dimension
{
	count : number;
	interval : number;
	offset : number;
	set : (v : Vector3, n : number) => void;
}
const setX = (v : Vector3, n : number) => v.x = n;
const setY = (v : Vector3, n : number) => v.y = n;
const setZ = (v : Vector3, n : number) => v.z = n;
const theFourthDimension : Dimension = { count : 1, interval : 0, offset : 0, set : (v,n) => {} }

const initDimension = (count : number, size : number, interval : number, align : number, set :  (v : Vector3, n : number) => void ) =>
{
	if(interval == 0) //Auto-Interval, count AND size must be known.
		return { count, interval : (count > 1) ? size / (count-1) : 0, offset : align * size, set };

	if(count != 0) //Interval and count known : auto-size
		return { count, interval, offset : interval * (count - 1) * align, set };

	if(size != 0) //Interval and size known : auto-count
		return { count : 1 + Math.floor(size / interval), interval, offset : align * size, set };

	//Only interval known : auto-count depending on the total number of items and the other dimensions.
	return {
		count : NaN, //Will be computed later
		interval, 
		offset : interval * align, //Will be multiplied by (count-1) later.
		set }; 
}

const oldOrNew = (oldV : number, newV : number) => isNaN(oldV) ? newV : oldV;

registerComponent("grid", {
	schema : {
		width : { type : "number", default : NaN }, //Size in x axis (OLD)
		height : { type : "number", default : NaN }, //Size in y axis (OLD)
		depth : { type : "number", default : NaN }, //Size in z axis (OLD)

		columns : { type : "int", default : NaN }, //Number of divisions of width (OLD)
		rows : { type : "int", default : NaN }, //Number of divisions of height (OLD)
		layers : { type : "int", default : NaN }, //Number of divisions of depth (OLD)

		size : { type : "vec3", default : { x:0, y:0, z:0 } }, //Size in x/y/z axis (NEW)
		interval : { type : "vec3", default : { x:0, y:0, z:0 } }, //Interval between items in x/y/z axis (NEW)
		dimension : { type : "vec3", default: { x:1, y:1, z:1 } }, //Number of divisions (NEW)
		align : { type : "vec3", default : { x:0.5, y:0.5, z:0.5 } },

		radius : { type : "vec2", default:  { x:  0, y : 0 }}, // TODO (or not) : z radius for spiral layout

		offset : { type : "int" }, //Index of first visible child
		
		order : { type : "array", default : ["column", "row", "layer" ]}
	},
	init(this : Component & { updateNeeded? : boolean })
	{
		this.el.addEventListener("child-attached", () => this.updateNeeded = true );
		this.el.addEventListener("child-detached", () => this.updateNeeded = true );
	},
	tick(this : Component & { updateNeeded? : boolean })
	{
		if(this.updateNeeded)
		{
			delete this.updateNeeded;
			this.update(null);
		}
	},
	update(this: Component & { data : GridData })
	{
		const xDimension : Dimension = initDimension(oldOrNew(this.data.columns, this.data.dimension.x), 
			oldOrNew(this.data.width, this.data.size.x), this.data.interval.x, this.data.align.x, setX );
		const yDimension : Dimension = initDimension(oldOrNew(this.data.rows, this.data.dimension.y), 
			oldOrNew(this.data.height, this.data.size.y), this.data.interval.y, this.data.align.y, setY );
		const zDimension : Dimension = initDimension(oldOrNew(this.data.layers, this.data.dimension.z), 
			oldOrNew(this.data.depth, this.data.size.z), this.data.interval.z, this.data.align.z, setZ );

		const dimensions : any = { 
			column : xDimension, width : xDimension, x : xDimension,
			row : yDimension, height : yDimension, y : yDimension,
			layer : zDimension, depth : zDimension, z : zDimension
		};
		const order : Dimension[] = this.data.order.map( (o : string) => dimensions[o.toLowerCase()] || theFourthDimension );

		let nUnknown = 0, maxCount = 1;

		for(let dim of order)
		{
			if(isNaN(dim.count)) ++nUnknown;
			else maxCount *= dim.count;
		}

		if(nUnknown > 0) //Some dimensions are "auto-count"
		{
			let totalCount = 0;
			for(let child = this.el.firstElementChild; child; child = child.nextElementSibling)
				if((child as Entity).object3D)
					++totalCount;
			totalCount = Math.ceil(totalCount / maxCount);

			for(let dim of order)
			{
				if(!isNaN(dim.count)) continue;

				const n = Math.ceil(Math.pow(totalCount, 1.0 / nUnknown));
				dim.count = n;
				dim.offset *= (n - 1);
				totalCount = Math.ceil(totalCount / n);
				maxCount *= n;
				if( --nUnknown <= 0 ) break; //Finished!
			}
		}


		


		let index = -this.data.offset;
		const rx = this.data.radius.x, ry = this.data.radius.y;

		for(let child = this.el.firstElementChild; child; child = child.nextElementSibling)
		{
			const el = child as Entity;
			if(!el.object3D) continue;

			const visible = (index >= 0) && (index < maxCount);
			el.object3D.visible = visible;
			("ammo-body" in el.components) && el.setAttribute("ammo-body", { enabled : visible });

			if( visible )
			{
				const pos = el.object3D.position;

				let ix = index;
				for(let o of order)
					if(o.count > 1)
					{
						o.set( pos, o.interval * (ix % o.count) - o.offset );
						ix = Math.floor(ix / o.count);
					}
					else
						o.set( pos, 0 );

				
				if(rx || ry)
				{
					const theta = rx ? (pos.x / rx) : 0, phi = ry ? (pos.y / ry) : 0;
					const cosTheta = Math.cos(theta), cosPhi = Math.cos(phi);
					pos.set(
						rx ? (rx - pos.z) * Math.sin(theta) : pos.x,
						ry ? (ry - pos.z) * Math.sin(phi) : pos.y,
						pos.z * cosTheta * cosPhi + rx * (1 - cosTheta) + ry * (1 - cosPhi)
					);
					el.object3D.rotation.set(phi, -theta, 0);
				}
				else
					el.object3D.rotation.set(0,0,0);
			}

			++index;
		}
	}
})
